import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaPhone,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaArrowRight,
  FaInstagram,
  FaDirections,
  FaCheckCircle,
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import { branchesData } from '../data/branchesData';

const ContactUs = () => {
  const form = useRef();

  const sendEmail = (e) => {
    e.preventDefault();
    emailjs.sendForm('service_h9g0btg', 'template_5urckpa', form.current, 'GJAtl73WZaFyf9KlA')
      .then(() => {
        alert('Message sent successfully! Our team will get back to you shortly.');
        form.current.reset();
      }, (error) => {
        console.log(error.text);
        alert('Something went wrong. Please try again or WhatsApp us directly.');
      });
  };

  const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
  const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

  return (
    <div className="px-4 md:px-12 py-12 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-16">
      {/* Header */}
      <motion.div
        className="text-center max-w-3xl mx-auto"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <span className="inline-block bg-red-600/20 text-red-500 border border-red-600/30 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
          IDOL FITNESS ZONE • 3 Branches in Coimbatore
        </span>
        <div className="text-3xl sm:text-5xl md:text-6xl font-black uppercase tracking-wide mb-4">
          <span className="text-transparent [-webkit-text-stroke:_1px_white]">GET IN </span>
          <span>TOUCH & VISIT US</span>
        </div>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
          Have questions or ready to begin your fitness transformation? Visit any of our 3 state-of-the-art gym units, connect on Instagram, or chat directly with our head trainers.
        </p>
      </motion.div>

      {/* ── 3 BRANCHES / UNITS SPOTLIGHT ── */}
      <section className="flex flex-col gap-8">
        <div className="flex items-center gap-4 justify-center">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-red-600/60" />
          <span className="text-red-500 text-xs font-bold uppercase tracking-[0.3em] flex items-center gap-2">
            <FaMapMarkerAlt /> Explore Our 3 Locations
          </span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-red-600/60" />
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          variants={stagger}
          viewport={{ once: true }}
        >
          {branchesData.map((branch, i) => (
            <motion.div
              key={branch.id}
              className="group relative bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 hover:border-red-500/50 p-7 sm:p-8 flex flex-col justify-between shadow-2xl transition-all duration-300 overflow-hidden"
              variants={fadeUp}
              whileHover={{ y: -8 }}
            >
              {/* Top color accent */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-600 via-rose-500 to-red-700" />

              <div>
                {/* Badge Row */}
                <div className="flex items-center justify-between gap-2 mb-4">
                  <span className="bg-red-600 text-white text-xs font-black uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md shadow-red-600/30">
                    {branch.unitNumber}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-red-400 bg-red-600/10 px-3 py-1 rounded-full border border-red-500/20">
                    {branch.badge}
                  </span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-white group-hover:text-red-400 transition-colors uppercase leading-tight mb-2">
                  {branch.name}
                </h3>

                <div className="flex items-start gap-2.5 text-xs text-gray-300 mb-6">
                  <FaMapMarkerAlt className="text-red-500 text-sm flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{branch.address}</span>
                </div>

                {/* Features List */}
                <div className="flex flex-col gap-2 mb-6 pt-4 border-t border-white/10">
                  <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Facility Highlights:</span>
                  {branch.features.map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-gray-300">
                      <FaCheckCircle className="text-red-500 text-[11px] flex-shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
                <a
                  href={branch.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2.5 text-xs uppercase tracking-wider transition-all shadow-lg shadow-red-600/20"
                >
                  <FaDirections className="text-sm" /> Open in Google Maps
                </a>

                <a
                  href={branch.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-white/5 hover:bg-white/15 text-white font-semibold py-2.5 px-4 rounded-xl border border-white/15 flex items-center justify-center gap-2 text-xs transition-all"
                >
                  <FaInstagram className="text-red-500 text-sm" /> Follow {branch.instaHandle}
                </a>

                <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1 px-1">
                  <span>📞 {branch.phone}</span>
                  <a href={branch.whatsapp} target="_blank" rel="noreferrer" className="text-green-400 hover:underline flex items-center gap-1 font-semibold">
                    <FaWhatsapp /> Chat
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── CLIENT JOURNEY 7-STEP CONNECTED ROADMAP ── */}
      <section className="flex flex-col gap-10 py-6 relative overflow-hidden" id="client-journey">
        {/* Glow ambient background */}
        <div className="absolute w-80 h-80 bg-red-600/15 blur-[160px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none -z-10" />

        <motion.div
          className="text-center max-w-3xl mx-auto flex flex-col items-center gap-3"
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600/20 to-red-900/20 border border-red-500/30 text-red-400 text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">
            <FaCheckCircle className="text-red-500" /> 7-Step Member Roadmap
          </span>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-wide text-white">
            CLIENT <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-red-600">JOURNEY</span>
          </h2>
          <p className="text-gray-300 text-xs sm:text-sm max-w-2xl leading-relaxed">
            Our 7-step guidance framework designed to guide every member from initial goal-setting to sustainable long-term athletic capacity.
          </p>
        </motion.div>

        {/* High-End Connected Step Flow Timeline Cards */}
        <div className="relative max-w-6xl mx-auto w-full">
          {/* Central Connecting Gradient Line on Desktop */}
          <div className="hidden lg:block absolute top-1/2 left-4 right-4 h-1 bg-gradient-to-r from-red-600/40 via-red-500 to-red-600/40 -translate-y-1/2 z-0 rounded-full" />

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-7 gap-4 lg:gap-3 relative z-10">
            {[
              { step: '01', title: 'Consultation', description: 'Understand the person and the goal.', tag: 'Phase 1' },
              { step: '02', title: 'Assessment', description: 'Establish a useful starting point.', tag: 'Phase 1' },
              { step: '03', title: 'Program Design', description: 'Build the training strategy.', tag: 'Phase 2' },
              { step: '04', title: 'Coaching', description: 'Teach execution & guidance.', tag: 'Phase 2' },
              { step: '05', title: 'Tracking', description: 'Measure meaningful progress.', tag: 'Phase 3' },
              { step: '06', title: 'Adjustment', description: 'Adapt the plan when required.', tag: 'Phase 3' },
              { step: '07', title: 'Progress', description: 'Build long-term capacity.', tag: 'Phase 4' },
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                className="group relative bg-[#13151f]/95 hover:bg-[#1a1c28] border border-white/10 hover:border-red-500/70 p-5 rounded-3xl flex flex-col justify-between gap-3 shadow-2xl transition-all duration-300 backdrop-blur-xl"
                initial="hidden"
                whileInView="visible"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: idx * 0.08 } }
                }}
                viewport={{ once: true }}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                {/* Step Top Glowing Indicator */}
                <div className="flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-red-600 to-red-800 text-white font-black text-xs flex items-center justify-center shadow-md shadow-red-600/40 group-hover:scale-110 transition-transform">
                    {item.step}
                  </span>
                  <span className="text-[9px] font-extrabold uppercase text-red-400 bg-red-600/10 px-2 py-0.5 rounded-md border border-red-500/20">
                    {item.tag}
                  </span>
                </div>

                <div className="flex flex-col gap-1.5 pt-1">
                  <h3 className="text-sm font-black uppercase text-white group-hover:text-red-400 transition-colors leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-[11px] text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Direct Contacts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            icon: <FaPhone />,
            title: 'Manager Sanjay',
            info: '73395 04861',
            sub: 'Call for Membership / Inquiries',
            href: 'tel:7339504861',
            color: '#e51b24',
          },
          {
            icon: <FaPhone />,
            title: 'Trainer Karthikeyan',
            info: '90474 77177',
            sub: 'Call for Training & Coaching',
            href: 'tel:9047477177',
            color: '#ef233c',
          },
          {
            icon: <FaWhatsapp />,
            title: 'WhatsApp Sanjay',
            info: 'Quick WhatsApp Reply',
            sub: '+91 73395 04861',
            href: 'https://wa.me/917339504861?text=Hi%20Sanjay,%20I%20would%20like%20to%20know%20about%20Idol%20Fitness%20Zone!',
            color: '#25d366',
          },
          {
            icon: <FaClock />,
            title: 'Working Hours',
            info: 'Mon – Sat: 5 AM – 10 PM',
            sub: 'Morning & Evening Sessions',
            href: '#',
            color: '#3498db',
          },
        ].map((card, i) => (
          <motion.a
            key={i}
            href={card.href}
            target={card.href.startsWith('http') ? '_blank' : undefined}
            rel={card.href.startsWith('http') ? 'noreferrer' : undefined}
            className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 flex flex-col gap-3 hover:border-red-500/50 transition-all duration-300 shadow-xl group"
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
          >
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center text-xl mb-1 transition-transform group-hover:scale-110"
              style={{ color: card.color, backgroundColor: `${card.color}18` }}
            >
              {card.icon}
            </span>
            <span className="text-base font-bold text-white group-hover:text-red-400 transition-colors">{card.title}</span>
            <span className="text-sm font-semibold text-gray-200">{card.info}</span>
            <span className="text-xs text-gray-400">{card.sub}</span>
          </motion.a>
        ))}
      </div>

      {/* Form Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl">
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          <div className="text-3xl sm:text-4xl font-black uppercase leading-tight">
            <span className="text-transparent [-webkit-text-stroke:_1px_white]">READY TO</span>
            <span> TRANSFORM</span>
            <br />
            <span>YOUR PHYSIQUE</span>
            <span className="text-transparent [-webkit-text-stroke:_1px_white]"> WITH US?</span>
          </div>
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
            Whether you want weight loss, muscle gain, strength training, or personal coaching, IDOL FITNESS ZONE provides the right environment across 3 convenient Coimbatore branches.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <a
              href="https://wa.me/917339504861?text=Hi%20I'm%20interested%20in%20joining%20Idol%20Fitness%20Zone!"
              target="_blank"
              rel="noreferrer"
              className="bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3.5 rounded-xl flex items-center justify-center gap-3 transition-colors text-xs uppercase tracking-wider shadow-lg"
            >
              <FaWhatsapp className="text-lg" /> Chat with Sanjay
            </a>
            <a
              href="https://wa.me/919047477177?text=Hi%20I'm%20interested%20in%20joining%20Idol%20Fitness%20Zone!"
              target="_blank"
              rel="noreferrer"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold px-6 py-3.5 rounded-xl flex items-center justify-center gap-3 transition-colors text-xs uppercase tracking-wider"
            >
              <FaWhatsapp className="text-lg text-green-400" /> Chat with Karthikeyan
            </a>
          </div>
        </motion.div>

        <motion.form
          ref={form}
          className="flex flex-col gap-4 bg-white/5 p-6 md:p-8 rounded-2xl border border-white/10"
          onSubmit={sendEmail}
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-white mb-2">Send Us a Direct Message</h3>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-gray-300" htmlFor="contact-name">Your Name</label>
            <input
              type="text" id="contact-name" name="user_name" placeholder="John Doe" required
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-red-500"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-gray-300" htmlFor="contact-email">Email Address</label>
            <input
              type="email" id="contact-email" name="user_email" placeholder="john@example.com" required
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-red-500"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-gray-300" htmlFor="contact-branch">Preferred Branch</label>
            <select
              id="contact-branch"
              name="branch"
              className="bg-[#1c1e24] border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-red-500"
            >
              <option value="Unit 1 - Chettipalayam JJ Nagar">Unit 1 - Chettipalayam JJ Nagar</option>
              <option value="Unit 2 - Pappampatti pirivu">Unit 2 - Pappampatti pirivu</option>
              <option value="Unit 3 - Peedampalli">Unit 3 - Peedampalli</option>
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-gray-300" htmlFor="contact-phone">Phone Number</label>
            <input
              type="tel" id="contact-phone" name="user_phone" placeholder="+91 XXXXX XXXXX"
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-red-500"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-gray-300" htmlFor="contact-msg">Your Fitness Goal</label>
            <textarea
              id="contact-msg" name="message" placeholder="Weight loss, muscle building, personal training..." rows="3" required
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-red-500"
            ></textarea>
          </div>
          <button
            type="submit"
            className="bg-red-600 hover:bg-red-500 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-wider cursor-pointer mt-2 shadow-lg shadow-red-600/30"
          >
            Start Your Fitness Journey <FaArrowRight />
          </button>
        </motion.form>
      </div>
    </div>
  );
};

export default ContactUs;
